import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { FieldData } from '../types';

interface FieldEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  field: FieldData | null;
  fieldHistory: {
    source: string;
    value: string | number;
  }[];
  onSave: (value: string | number, feedback?: { textFeedback: string; screenshot?: File }) => void;
}

export const FieldEditDialog: React.FC<FieldEditDialogProps> = ({
  isOpen,
  onClose,
  field,
  fieldHistory,
  onSave
}) => {
  const [selectedValue, setSelectedValue] = useState<string>('current');
  const [customValue, setCustomValue] = useState<string>('');
  const [textFeedback, setTextFeedback] = useState<string>('');
  const [screenshot, setScreenshot] = useState<File | null>(null);

  useEffect(() => {
    setCustomValue('');
  }, [field?.value]);

  useEffect(() => {
    setTextFeedback('');
  }, [field]);

  if (!field) return null; 
  const handleSave = () => {
    let valueToSave: string | number = field.value;
    
    if (selectedValue === 'custom') {
      valueToSave = customValue;
    } else if (selectedValue === 'expected') {
      valueToSave = field.expectedValue || field.value;
    } else if (selectedValue !== 'current') {
      const historyItem = fieldHistory.find(h => h.source === selectedValue);
      if (historyItem) {
        valueToSave = historyItem.value;
      }
    }
    
    const feedback = textFeedback || screenshot ? {
      textFeedback,
      screenshot: screenshot || undefined
    } : undefined;
    
    onSave(valueToSave, feedback);
    onClose();
  };

  const handleScreenshotUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setScreenshot(file);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Field: {field.key}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Current Value</Label>
            <Input value={field.value} readOnly className="bg-gray-50" />
          </div>
          
          <div className="space-y-2">
            <Label>Select Value</Label>
            <RadioGroup value={selectedValue} onValueChange={setSelectedValue}>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="current" id="current" />
                  <Label htmlFor="current" className="font-normal cursor-pointer">
                    Current Value: {field.value}
                  </Label>
                </div>
                
                {fieldHistory.map((history, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <RadioGroupItem value={history.source} id={history.source} />
                    <Label htmlFor={history.source} className="font-normal cursor-pointer">
                      {history.source}: {history.value}
                    </Label>
                  </div>
                ))}
                
                {field.expectedValue && field.expectedValue !== 'None' && 
                 !fieldHistory.some(h => h.source === 'Validation Database') && (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="expected" id="expected" />
                    <Label htmlFor="expected" className="font-normal cursor-pointer">
                      Database Expected: {field.expectedValue}
                    </Label>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="font-normal cursor-pointer">
                    Custom Value
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>
          
          {selectedValue === 'custom' && (
            <div className="space-y-2">
              <Label>Enter Custom Value</Label>
              <Input
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="Enter new value..."
              />
            </div>
          )}
          
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <Label className="text-base font-medium">Coach the Agents</Label>
            
            <div className="space-y-2">
              <Label>Additional Text Feedback</Label>
              <textarea
                value={textFeedback}
                onChange={(e) => setTextFeedback(e.target.value)}
                placeholder="Provide feedback to help improve agent performance..."
                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Screenshot Upload</Label>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              {screenshot && (
                <p className="text-sm text-green-600">Screenshot uploaded: {screenshot.name}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};